import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, ChevronDown, ChevronLeft, Clock, Link2, ListOrdered, Share2 } from "lucide-react";
import { api } from "../lib/api";
import BlogBody, { tocFromBlocks } from "../components/blog/BlogBody";
import BlogSidebar from "../components/blog/BlogSidebar";
import SiteBanners from "../components/SiteBanners";
import { absolute, applySeo, articleJsonLd, breadcrumbJsonLd, faqJsonLd } from "../lib/seoHead";
import { useSeoSettings } from "../lib/useSeoSettings";
import AuthorBox from "../components/blog/AuthorBox";
import { useBlogPreview } from "../lib/blogPreview";
import { offPreviewRequest, offReason } from "../lib/offPreview";
import { OffPreviewBanner } from "../components/OffPreviewBanner";
import { PageLoader } from "../components/LoadingRing";

const dateLabel = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "");

export default function BlogDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [tocOpen, setTocOpen] = React.useState(false);
    const { isPreview, draft } = useBlogPreview();

    const { data: fetched, isLoading, isError, error } = useQuery({
        queryKey: ["blog-post", slug],
        queryFn: async () => (await api.get(`/blog/posts/${slug}`, offPreviewRequest())).data,
        retry: false,
        enabled: !isPreview,
    });
    const post = isPreview ? draft : fetched;
    const { data: meta } = useQuery({ queryKey: ["blog-meta"], queryFn: async () => (await api.get("/blog/meta")).data });

    const track = React.useCallback((type, target = "") => {
        if (isPreview) return;
        api.post("/blog/track", { slug, type, target, source: `blog:${slug}` }).catch(() => {});
    }, [slug, isPreview]);

    const brand = useSeoSettings();

    React.useEffect(() => {
        if (post?.redirect_to && !isPreview) navigate(`/blog/${post.redirect_to}`, { replace: true });
    }, [post, navigate, isPreview]);

    // An article whose Live switch is off sends visitors back to the blog index.
    React.useEffect(() => {
        if (offReason(error) === "post_off") navigate("/blog", { replace: true });
    }, [error, navigate]);

    React.useEffect(() => {
        if (!post?.id || isPreview) return;
        track("view");
    }, [post?.id, track, isPreview]);

    React.useEffect(() => {
        if (!post?.id || isPreview) return;
        const url = post.seo?.canonical_url || `${window.location.origin}/blog/${post.slug}`;
        applySeo({
            title: post.seo?.seo_title || `${post.title} — ReHealthMe`,
            description: post.seo?.meta_description || post.excerpt || "",
            canonical: url,
            image: post.social_image ? absolute(post.social_image)
                : post.featured_image?.url ? absolute(post.featured_image.url) : "",
            noindex: !!post.seo?.noindex,
            jsonLd: [articleJsonLd(post, url, brand), breadcrumbJsonLd(post, url), faqJsonLd(post)],
        });
    }, [post, isPreview, brand]);

    if (isPreview && !post) {
        return <PageLoader label="WAITING FOR THE EDITOR…" className="pt-32 pb-24" />;
    }
    if (!isPreview && isLoading) return <PageLoader label="LOADING ARTICLE…" className="pt-32 pb-24" />;
    if (!isPreview && (isError || !post?.id)) {
        return (
            <div className="pt-32 pb-24 text-center" data-testid="blog-not-found">
                <div className="font-display text-3xl uppercase tracking-tight">Article not found</div>
                <Link to="/blog" className="mt-4 inline-flex items-center gap-2 mono text-[10px] tracking-[0.16em] text-gold">
                    <ChevronLeft className="w-3.5 h-3.5" /> ALL ARTICLES
                </Link>
            </div>
        );
    }

    const toc = post.toc_enabled ? tocFromBlocks(post.blocks) : [];
    const author = post.author || {};
    const reviewed = post.review?.status === "reviewed";
    const editorial = post.style === "editorial";

    const share = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) await navigator.share({ title: post.title, url });
            else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
            track("share", url);
        } catch { /* dismissed */ }
    };

    return (
        <article data-testid="blog-detail-page">
            {post.page_live === false && <OffPreviewBanner label={post.title} />}
            <SiteBanners placement="blog" />
            <div className="pt-24 md:pt-28 pb-20">
                <div className="mx-auto max-w-7xl px-6 md:px-12">
                    <nav className="mono text-[10px] tracking-[0.16em] text-zinc-500 flex flex-wrap items-center gap-2"
                        data-testid="blog-breadcrumbs">
                        <Link to="/" className="hover:text-gold">HOME</Link><span>/</span>
                        <Link to="/blog" className="hover:text-gold">JOURNAL</Link>
                        {post.category && (<><span>/</span>
                            <Link to={`/blog/category/${post.category.slug}`} className="hover:text-gold">
                                {post.category.name.toUpperCase()}</Link></>)}
                    </nav>

                    <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-10 lg:gap-14 mt-6">
                        <div className="min-w-0">
                            <header>
                                {post.category && (
                                    <Link to={`/blog/category/${post.category.slug}`}
                                        className="inline-block bg-gold/15 text-gold px-3 py-1 mono text-[9px] tracking-[0.2em]"
                                        data-testid="blog-category-badge">{post.category.name.toUpperCase()}</Link>
                                )}
                                <h1 className={`mt-4 font-display uppercase tracking-tight ${
                                    editorial ? "text-4xl sm:text-5xl lg:text-6xl" : "text-4xl sm:text-5xl"}`}
                                    data-testid="blog-title">{post.title}</h1>
                                {post.excerpt && <p className="mt-4 text-zinc-400 text-base md:text-lg max-w-3xl">{post.excerpt}</p>}

                                <div className="mt-6 flex flex-wrap items-center gap-4 border-y border-white/10 py-4">
                                    <div className="flex items-center gap-3 min-w-0" data-testid="blog-author">
                                        {author.photo_url ? (
                                            <img src={author.photo_url} alt={author.name}
                                                className="w-10 h-10 rounded-full object-cover border border-white/10" />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-gold/15 text-gold grid place-items-center font-display">
                                                {(author.name || "R")[0]}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold truncate">
                                                {author.is_brand ? author.name : `By ${author.name || "ReHealthMe"}`}
                                            </div>
                                            <div className="mono text-[9px] tracking-[0.16em] text-zinc-500 truncate">
                                                {author.title || "EDITORIAL"}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mono text-[10px] tracking-[0.16em] text-zinc-500 flex items-center gap-4 ml-auto">
                                        <span>{dateLabel(post.published_at)}</span>
                                        <span className="inline-flex items-center gap-1.5">
                                            <Clock className="w-3.5 h-3.5" /> {post.reading_minutes} MIN READ
                                        </span>
                                        <button onClick={share} data-testid="blog-share"
                                            className="inline-flex items-center gap-1.5 hover:text-gold">
                                            <Share2 className="w-3.5 h-3.5" /> SHARE
                                        </button>
                                    </div>
                                </div>

                                {reviewed && (
                                    <div className="mt-4 inline-flex items-center gap-2 border border-lime/30 bg-lime/[0.07]
                                                    px-3 py-2 mono text-[10px] tracking-[0.16em] text-lime"
                                        data-testid="blog-reviewed">
                                        <BadgeCheck className="w-4 h-4" />
                                        REVIEWED BY {(post.review.reviewer || "A QUALIFIED PROFESSIONAL").toUpperCase()}
                                    </div>
                                )}

                                {post.featured_image?.url && (
                                    <figure className="mt-8">
                                        <img src={post.featured_image.url} alt={post.featured_image.alt || post.title}
                                            className="w-full border border-white/10 aspect-[16/9] object-cover"
                                            data-testid="blog-featured-image" />
                                        {post.featured_image.caption && (
                                            <figcaption className="mt-2 text-xs text-zinc-500">{post.featured_image.caption}</figcaption>
                                        )}
                                    </figure>
                                )}
                            </header>

                            {toc.length >= 2 && (
                                <nav className="mt-8 border border-white/10 bg-bg1" data-testid="blog-toc">
                                    <button onClick={() => setTocOpen((v) => !v)} data-testid="blog-toc-toggle"
                                        className="w-full flex items-center justify-between gap-3 px-5 py-4">
                                        <span className="mono text-[10px] tracking-[0.2em] text-gold inline-flex items-center gap-2">
                                            <ListOrdered className="w-3.5 h-3.5" /> IN THIS ARTICLE
                                        </span>
                                        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform lg:hidden ${tocOpen ? "rotate-180" : ""}`} />
                                    </button>
                                    <ol className={`px-5 pb-5 space-y-2 ${tocOpen ? "block" : "hidden lg:block"}`}>
                                        {toc.map((t, i) => (
                                            <li key={t.id} className={t.level === 3 ? "pl-5" : ""}>
                                                <a href={`#${t.id}`} data-testid={`blog-toc-link-${i}`}
                                                    className="text-sm text-zinc-300 hover:text-gold transition-colors">
                                                    <span className="mono text-[10px] text-zinc-600 mr-2">
                                                        {String(i + 1).padStart(2, "0")}
                                                    </span>{t.text}
                                                </a>
                                            </li>
                                        ))}
                                    </ol>
                                </nav>
                            )}

                            <div className="mt-8">
                                <BlogBody blocks={post.blocks || []} style={post.style} onTrack={track} />
                            </div>

                            {!!(post.tags || []).length && (
                                <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-tags">
                                    {post.tags.map((t) => (
                                        <Link key={t} to={`/blog?q=${encodeURIComponent(t)}`}
                                            className="border border-white/12 px-3 py-1.5 text-xs text-zinc-400 hover:border-gold hover:text-gold">
                                            #{t}
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {post.disclaimer && (
                                <div className="mt-10 border-l-2 border-white/20 pl-4 text-xs text-zinc-500 leading-relaxed"
                                    data-testid="blog-disclaimer">{post.disclaimer}</div>
                            )}

                            <AuthorBox author={author} reviewer={post.reviewer}
                                settings={post.settings || {}}
                                reviewedAt={post.updated_at || post.published_at} />

                            {!!(post.related || []).length && (
                                <section className="mt-14" data-testid="blog-related">
                                    <div className="eyebrow mb-5">You may also like</div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        {post.related.map((r) => (
                                            <Link key={r.id} to={`/blog/${r.slug}`} data-testid={`blog-related-${r.slug}`}
                                                className="group border border-white/10 bg-bg1 hover:border-gold/40 transition-colors flex gap-4 p-4">
                                                <div className="w-20 h-20 shrink-0 bg-bg0 overflow-hidden">
                                                    {r.featured_image?.url && (
                                                        <img src={r.featured_image.url} alt={r.featured_image.alt || r.title}
                                                            loading="lazy" className="w-full h-full object-cover" />
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="mono text-[9px] tracking-[0.16em] text-gold">
                                                        {r.category?.name?.toUpperCase()}
                                                    </div>
                                                    <div className="text-sm mt-1 group-hover:text-gold transition-colors line-clamp-2">{r.title}</div>
                                                    <div className="mono text-[9px] tracking-[0.14em] text-zinc-500 mt-1.5">
                                                        <Link2 className="w-3 h-3 inline mr-1" />{r.reading_minutes} MIN READ
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </div>

                        {post.settings?.sidebar_enabled !== false && (
                            <aside className="lg:sticky lg:top-24 self-start">
                                <BlogSidebar post={post} popular={post.popular || []}
                                    categories={meta?.categories || []} onTrack={track}
                                    newsletter={post.settings?.newsletter_enabled !== false} />
                            </aside>
                        )}
                    </div>
                </div>
            </div>
        </article>
    );
}
